import React, { Component } from "react";
import PropTypes from "prop-types";

import GenericChartComponent from "../../GenericChartComponent";
import { getMouseCanvas } from "../../GenericComponent";
import marketdata from "../../../../data/Services/marketdata.jsx";

// Position Line component - renders horizontal line for current positions
class PositionLine extends Component {
  constructor(props) {
    super(props);
    this.renderSVG = this.renderSVG.bind(this);
    this.drawOnCanvas = this.drawOnCanvas.bind(this);
    this.calculatePnL = this.calculatePnL.bind(this);
    this.getPnLColor = this.getPnLColor.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.position?.quantity !== this.props.position?.quantity ||
      prevProps.position?.price !== this.props.position?.price
    ) {
      if (this.context?.redraw) {
        this.context.redraw();
      }
    }
  }

  componentWillUnmount() {
    const redraw = this.context?.redraw;
    if (redraw) {
      setTimeout(() => redraw(), 0);
    }
  }

  // Calculate P&L for position (same logic as usePositionsData).
  // Retorna null quando o FOQ do servidor não está disponível: multiplicador é
  // spec do contrato e NUNCA é fabricado no cliente — sem dado, sem R$.
  calculatePnL(position, currentPrice) {
    if (!position || !position.quantity) return 0;

    const foq = marketdata?.getFOQ?.(position.symbol);
    if (foq === null || foq === undefined) return null;

    const { quantity, price } = position;
    const absQ = Math.abs(quantity);
    const entryLot = price * absQ;
    const currentLot = currentPrice * absQ;

    let net = 0;
    if (quantity > 0) net = currentLot - entryLot;
    else if (quantity < 0) net = entryLot - currentLot;

    return net * foq;
  }

  // Get color based on P&L (green for profit, red for loss)
  getPnLColor(pnl) {
    if (pnl === null) return "#6b7280"; // FOQ indisponível → neutro, sem afirmar P&L
    if (pnl > 0) return "#16a34a"; // Green for profit
    if (pnl < 0) return "#dc2626"; // Red for loss
    return "#6b7280"; // Gray for neutral
  }

  drawOnCanvas(ctx, moreProps) {
    const { position } = this.props;

    if (!position || !position.quantity || position.quantity === 0) {
      return; // No position to display
    }

    const {
      chartConfig: { width, yScale },
    } = moreProps;

    if (!yScale || typeof yScale !== "function" || !position.price) {
      return;
    }

    // Get the visible chart range
    const range = yScale.range();

    // Get current market price for P&L calculation
    let currentPrice = position.price;
    if (marketdata && position.symbol) {
      const lastPrice = marketdata.getLastQuote(position.symbol)?.C;
      if (typeof lastPrice === "number" && isFinite(lastPrice)) {
        currentPrice = lastPrice;
      }
    }

    // Calculate P&L and determine color
    const pnl = this.calculatePnL(position, currentPrice);
    const lineColor = this.getPnLColor(pnl);

    // Get Y position for the position price
    const y = yScale(position.price);

    // Check if the position is within the visible chart area
    const chartHeight = Math.abs(range[1] - range[0]);
    const isVisible = y >= 0 && y <= chartHeight;

    // Only draw if the position line would be visible
    if (!isVisible) {
      return;
    }

    // Set line style for position with P&L-based color - thin dashed line
    ctx.strokeStyle = lineColor;
    ctx.lineWidth = 1; // Very thin line
    ctx.setLineDash([4, 3]); // Small dashes for subtle appearance
    ctx.globalAlpha = 0.7; // Slightly transparent

    // Draw horizontal line across the chart
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(width, y);
    ctx.stroke();

    // Remove the circle - just keep the line
    // Reset canvas state
    ctx.setLineDash([]);
    ctx.globalAlpha = 1.0;
  }

  renderSVG() {
    return null; // Canvas-only implementation
  }

  render() {
    return (
      <GenericChartComponent
        canvasToDraw={getMouseCanvas}
        canvasDraw={this.drawOnCanvas}
        drawOn={[
          "mousemove",
          "mouseleave",
          "pan",
          "drag",
          "draw",
          "zoom",
          "xAxisZoom",
          "resize",
        ]}
        svgDraw={this.renderSVG}
      />
    );
  }
}

PositionLine.propTypes = {
  position: PropTypes.shape({
    quantity: PropTypes.number,
    price: PropTypes.number,
    basis: PropTypes.number,
    symbol: PropTypes.string,
  }),
};

PositionLine.defaultProps = {
  position: null,
};

PositionLine.contextTypes = {
  redraw: PropTypes.func,
};

export default PositionLine;
